import { useState, useRef } from "react";

export type PendingTabKey =
  | "pending-immediate"
  | "pending-on-deck"
  | "pending-today"
  | "done"
  | "pending-future"
  | `campaign-pending:${string}`
  | `campaign-done:${string}`;

interface UsePendingTabStateReturn {
  activeTabKey: PendingTabKey;
  onActiveTabChange: (key: string) => void;
}

/**
 * Custom hook to manage pending order tab state with auto-reset behavior.
 * Automatically resets to "pending-immediate" tab after 40 seconds of inactivity
 * when viewing other tabs.
 */
export const usePendingTabState = (): UsePendingTabStateReturn => {
  const [activeTabKey, setActiveTabKey] = useState<PendingTabKey>("pending-immediate");
  const activeTabInterval = useRef<NodeJS.Timeout | null>(null);

  const onActiveTabChange = (key: string) => {
    // Reset to pending-immediate tab automatically after 40 seconds
    // (requested by O2 for operational efficiency)
    if (activeTabInterval.current) {
      clearTimeout(activeTabInterval.current);
    }
    if (key !== "pending-immediate") {
      activeTabInterval.current = setTimeout(() => {
        setActiveTabKey("pending-immediate");
      }, 40 * 1000);
    }
    setActiveTabKey(key as PendingTabKey);
  };

  return {
    activeTabKey,
    onActiveTabChange,
  };
};
